"use client";
import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { groupBy, keys, sortBy } from "lodash";
import {
  TextInput,
  Select,
  UnstyledButton,
  Anchor as MantineAnchor,
  Box as MantineBox,
  Text as MantineText,
  Title as MantineTitle,
} from "@mantine/core";
import { useGlobalData } from "@/context/globalContext";
import { sendGtagEvent } from "@/lib/client/gtag";
import { trackEvent } from "@/lib/client/analytics";

import styles from "./styles.module.scss";

const ProxiesList = () => {
  const { proxies: proxiesByCountry } = useGlobalData();
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<string | null>("country");
  const [expandedCountry, setExpandedCountry] = useState<string | null>(null);

  // Deep link support: /proxies?country=Japan preselects and expands that
  // country (used by the item slide-in's proxy link). Waits for the proxy
  // data so the param can be validated against real country names.
  const countryParam = searchParams.get("country");
  useEffect(() => {
    if (!countryParam || !proxiesByCountry?.length) return;
    const match = proxiesByCountry.find(
      (pc) => pc.countryname.toLowerCase() === countryParam.toLowerCase()
    );
    if (match) {
      setSelectedCountry(match.countryname);
      setExpandedCountry(match.countryname);
    }
  }, [countryParam, proxiesByCountry]);

  const filteredProxies = useMemo(() => {
    if (!proxiesByCountry) return [];

    return proxiesByCountry.filter((proxyCountry) => {
      if (selectedCountry && proxyCountry.countryname !== selectedCountry) {
        return false;
      }

      if (searchQuery) {
        const hasMatchingProxy = proxyCountry.proxies.some((proxy) =>
          proxy.proxyname.toLowerCase().includes(searchQuery.toLowerCase())
        );
        const countryMatches = proxyCountry.countryname
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

        if (!hasMatchingProxy && !countryMatches) {
          return false;
        }
      }

      return true;
    });
  }, [proxiesByCountry, searchQuery, selectedCountry]);

  const groupedProxies = useMemo(() => {
    if (orderBy === "alphabet") {
      const grouped = groupBy(filteredProxies, (proxyCountry) => {
        const firstChar = proxyCountry.countryname.charAt(0).toUpperCase();
        return /[A-Z]/.test(firstChar) ? firstChar : "Special";
      });
      return grouped as Record<string, ProxyCountry[]>;
    } else {
      const grouped = groupBy(filteredProxies, "countryname");
      return grouped as Record<string, ProxyCountry[]>;
    }
  }, [filteredProxies, orderBy]);

  const sortedGroups = useMemo(() => {
    const groups = keys(groupedProxies);

    if (orderBy === "alphabet") {
      return sortBy(groups, (group) => {
        if (group === "Special") return "!";
        return group;
      });
    } else {
      return sortBy(groups);
    }
  }, [groupedProxies, orderBy]);

  const countries = useMemo(() => {
    if (!proxiesByCountry) return [];
    const uniqueCountries = proxiesByCountry.map((pc) => pc.countryname);
    return sortBy(uniqueCountries).map((country) => ({
      value: country,
      label: country,
    }));
  }, [proxiesByCountry]);

  const toggleCountry = (country: string) => {
    setExpandedCountry(expandedCountry === country ? null : country);
  };

  if (!proxiesByCountry || proxiesByCountry.length === 0) {
    return (
      <MantineBox className={styles.introText}>
        <MantineText>Loading proxy information...</MantineText>
      </MantineBox>
    );
  }

  return (
    <>
      <MantineBox className={styles.introText}>
        <MantineText>
          If you're shopping internationally, many stores don't ship to your country or only accept
          local payment methods. Proxy and package forwarding services can help you purchase items
          from anywhere in the world. Below, services are organized by the{" "}
          <MantineText span fw={700}>
            country they ship from
          </MantineText>
          , so you can find proxies that receive packages locally and forward them to you.
        </MantineText>
        <MantineText>
          Learn more about how to use proxies in our{" "}
          <MantineAnchor href="/blog/how-to-buy-clothes-from-anywhere-in-the-world">
            comprehensive guide
          </MantineAnchor>
          .
        </MantineText>
        <MantineText>
          If you know of a proxy service we should add, reach out at{" "}
          <MantineAnchor href="mailto:contact@kurb.online">contact@kurb.online</MantineAnchor> or
          tell us on{" "}
          <MantineAnchor
            href="https://discord.gg/jSh7n6ju8H"
            target="_blank"
            rel="noopener noreferrer"
          >
            Discord
          </MantineAnchor>
          .
        </MantineText>
      </MantineBox>

      <MantineBox className={styles.filterControls}>
        <TextInput
          placeholder="Search proxies or countries"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          classNames={{
            root: styles.searchInput,
            input: styles.mantineInput,
          }}
        />

        <Select
          placeholder="Order by"
          value={orderBy}
          onChange={setOrderBy}
          data={[
            { value: "country", label: "Country" },
            { value: "alphabet", label: "Alphabetical" },
          ]}
          classNames={{
            root: styles.selectInput,
            input: styles.mantineInput,
          }}
          clearable
        />

        <Select
          placeholder="Filter by ship-from country"
          value={selectedCountry}
          onChange={setSelectedCountry}
          data={countries}
          classNames={{
            root: styles.selectInput,
            input: styles.mantineInput,
          }}
          searchable
          clearable
        />
      </MantineBox>

      <MantineBox className={styles.proxiesGrid}>
        <MantineBox>
          {sortedGroups.map((group) => (
            <MantineBox key={group} className={styles.regionSection}>
              <MantineTitle order={2} className={styles.regionHeader}>
                {group}
              </MantineTitle>
              <MantineBox className={styles.proxiesList}>
                {groupedProxies[group]?.map((proxyCountry) => (
                  <MantineBox key={proxyCountry.country_id} className={styles.countrySection}>
                    {orderBy === "alphabet" && (
                      <MantineBox className={styles.countryName}>
                        <UnstyledButton
                          className={styles.countryButton}
                          onClick={() => toggleCountry(proxyCountry.countryname)}
                          data-active={expandedCountry === proxyCountry.countryname}
                        >
                          <MantineText span className={styles.plusIcon}>
                            {expandedCountry === proxyCountry.countryname ? "⊖" : "⊕"}
                          </MantineText>
                          {proxyCountry.countryname}
                          <MantineText span className={styles.proxyCount}>
                            ({proxyCountry.proxies.length}{" "}
                            {proxyCountry.proxies.length === 1 ? "proxy" : "proxies"})
                          </MantineText>
                        </UnstyledButton>
                      </MantineBox>
                    )}

                    <MantineBox
                      className={styles.proxyItems}
                      data-visible={
                        orderBy !== "alphabet" ||
                        expandedCountry === proxyCountry.countryname ||
                        !!searchQuery
                      }
                    >
                      {proxyCountry.proxies
                        .filter((proxy) => {
                          if (!searchQuery) return true;
                          const countryMatches = proxyCountry.countryname
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase());
                          if (countryMatches) return true;
                          return proxy.proxyname.toLowerCase().includes(searchQuery.toLowerCase());
                        })
                        .map((proxy) => (
                          <MantineBox key={proxy.id} className={styles.proxyCard}>
                            <MantineAnchor
                              href={proxy.proxyurl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.proxyLink}
                              onClick={() => {
                                sendGtagEvent("proxy_outbound", "outbound", proxy.proxyname, {
                                  proxy: proxy.proxyname,
                                  country: proxyCountry.countryname,
                                });
                                try {
                                  trackEvent("proxy_outbound_click", "engagement", {
                                    proxy: proxy.proxyname,
                                    country: proxyCountry.countryname,
                                    source: "proxies_list",
                                  });
                                } catch {
                                  // Analytics must never affect outbound navigation
                                }
                              }}
                            >
                              <MantineText span className={styles.proxyName}>
                                {proxy.proxyname}
                                {proxy.proxyreccomended === 1 && (
                                  <MantineText span className={styles.recommendedBadge}>
                                    Recommended
                                  </MantineText>
                                )}
                              </MantineText>
                            </MantineAnchor>
                            {proxy.proxydescription && (
                              <MantineText className={styles.proxyDescription}>
                                {proxy.proxydescription}
                              </MantineText>
                            )}
                          </MantineBox>
                        ))}
                    </MantineBox>
                  </MantineBox>
                ))}
              </MantineBox>
            </MantineBox>
          ))}
        </MantineBox>
      </MantineBox>

      {filteredProxies.length === 0 && (
        <MantineBox className={styles.noResults}>
          <MantineText>No proxies found matching your search criteria.</MantineText>
        </MantineBox>
      )}
    </>
  );
};

export default ProxiesList;
